import { useAppState } from '~/app/contexts/app-context';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { createNewDataSection, filterConditionalEntries } from '~/app/utils/data-sections';
import { getTechnicalDataItemsModel } from '~/app/utils/get-technical-data-items';
import { ModelTechnicalDataQuery } from '~/graphql/__generated__/graphql';

export function useModelTechnicalDataSection(
	td: NonNullable<
		NonNullable<
			ModelTechnicalDataQuery['configuredCar']['carlineStructureCarline']
		>['trimlines'][0]['models'][0]['technicalDataV2']
	>,
) {
	const { i18n } = useI18n();
	const { market } = useMarketConfig();
	const { translations } = useAppState();
	const driveTrain = td?.modelAttributes?.driveTrain ?? 'BEV';

	const {
		engineData,
		performanceData,
		technicalData,
		drivelineData,
		landingGearData,
		brakeSystemData,
		steeringSystemData,
		weightsData,
		trailerWeightData,
		volumeData,
		consumptionData,
		noiseData,
		insuranceData,
		emissionData,
	} = getTechnicalDataItemsModel(td, market, driveTrain, i18n, translations);

	return filterConditionalEntries([
		engineData && createNewDataSection(i18n('ngc.technicalData.engine'), [engineData], translations),
		performanceData &&
			createNewDataSection(i18n('ngc.technicalData.performanceData'), performanceData, translations),
		technicalData &&
			createNewDataSection(
				driveTrain === 'ICE' || driveTrain === 'MHEV'
					? i18n('ngc.technicalData.combustionDrive')
					: i18n('ngc.technicalData.electricDrive'),
				technicalData,
				translations,
			),
		drivelineData && createNewDataSection(i18n('ngc.technicalData.driveline'), drivelineData, translations),
		landingGearData && createNewDataSection(i18n('ngc.technicalData.landingGear'), landingGearData, translations),
		brakeSystemData && createNewDataSection(i18n('ngc.technicalData.brakeSystem'), brakeSystemData, translations),
		steeringSystemData &&
			createNewDataSection(i18n('ngc.technicalData.steeringSystem'), steeringSystemData, translations),
		weightsData && createNewDataSection(i18n('ngc.technicalData.weights'), weightsData, translations),
		market !== 'TW' &&
			trailerWeightData &&
			createNewDataSection(i18n('ngc.technicalData.trailerWeightLimit'), trailerWeightData, translations),
		volumeData && createNewDataSection(i18n('ngc.technicalData.volumes'), volumeData, translations),
		consumptionData && createNewDataSection(i18n('ngc.technicalData.consumption'), consumptionData, translations),
		noiseData && createNewDataSection(i18n('ngc.technicalData.noiseLevel'), noiseData, translations),
		insuranceData && createNewDataSection(i18n('ngc.technicalData.insuranceClasses'), insuranceData, translations),
		emissionData &&
			createNewDataSection(i18n('ngc.technicalData.consumptionAndEmission'), emissionData, translations),
	]).filter((section) => section.entries.length > 0);
}
